/**
 * Model dropdown selector.
 *
 * Allows switching the AI model for the active session.
 * Shows a dropdown of available models grouped by provider
 * with the current selection highlighted.
 *
 * Models are grouped by provider (e.g., "Anthropic", "OpenAI")
 * using <optgroup> elements for clear visual separation.
 */

import { useMemo } from 'react';
import type { ModelCatalogEntry } from '../types/sessions';
import { formatProviderName } from '../utils/providers';
import styles from './ModelSelector.module.css';

interface ModelSelectorProps {
  /** Currently selected model ID */
  selectedModel: string;
  /** Called when the user selects a different model */
  onModelChange: (model: string) => void;
  /** Available models from the gateway (already filtered to auth-configured providers) */
  models?: ModelCatalogEntry[];
  /** Whether the selector is disabled (e.g., during active request) */
  disabled?: boolean;
}

export function ModelSelector({
  selectedModel,
  onModelChange,
  models,
  disabled = false,
}: ModelSelectorProps) {
  // Group models by provider
  const modelsByProvider = useMemo(() => {
    if (!models || models.length === 0) return [];

    const groups = new Map<string, ModelCatalogEntry[]>();
    for (const m of models) {
      const provider = m.provider || 'other';
      const list = groups.get(provider) ?? [];
      list.push(m);
      groups.set(provider, list);
    }

    // Sort providers alphabetically, with 'other' last
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'other') return 1;
      if (b === 'other') return -1;
      return a.localeCompare(b);
    });
  }, [models]);

  const hasModels = modelsByProvider.length > 0;

  return (
    <select
      className={styles.select}
      value={selectedModel}
      onChange={(e) => onModelChange(e.target.value)}
      disabled={disabled}
      aria-label="Select AI model"
    >
      <option value="default">Default Model</option>
      {hasModels
        ? modelsByProvider.map(([provider, providerModels]) => (
            <optgroup key={provider} label={formatProviderName(provider)}>
              {providerModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.name || m.id}
                </option>
              ))}
            </optgroup>
          ))
        : null}
    </select>
  );
}
